import * as React from "react";
import {
  BotIcon,
  CalendarIcon,
  FlagIcon,
  HourglassIcon,
  UserPlusIcon,
  type LucideIcon
} from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * The five Home item types. Each has one icon and one hue; hues mark the
 * type only (the icon, a runway marker) and are never backgrounds. Status
 * keeps the semantic tones, so red still means overdue.
 */
type Kind = "deadline" | "expiration" | "agent" | "intake" | "calendar";

const KINDS: Record<
  Kind,
  { label: string; icon: LucideIcon; text: string; bg: string }
> = {
  deadline: {
    label: "Deadlines",
    icon: FlagIcon,
    text: "text-kind-deadline",
    bg: "bg-kind-deadline"
  },
  expiration: {
    label: "Expirations",
    icon: HourglassIcon,
    text: "text-kind-expiration",
    bg: "bg-kind-expiration"
  },
  agent: {
    label: "Agent Activity",
    icon: BotIcon,
    text: "text-kind-agent",
    bg: "bg-kind-agent"
  },
  intake: {
    label: "Intakes and Requests",
    icon: UserPlusIcon,
    text: "text-kind-intake",
    bg: "bg-kind-intake"
  },
  calendar: {
    label: "Calendar",
    icon: CalendarIcon,
    text: "text-kind-calendar",
    bg: "bg-kind-calendar"
  }
};

const KIND_ORDER: Kind[] = [
  "deadline",
  "expiration",
  "agent",
  "intake",
  "calendar"
];

type KindIconProps = Omit<React.SVGProps<SVGSVGElement>, "ref"> & {
  kind: Kind;
};

function KindIcon({ kind, className, ...props }: KindIconProps) {
  const { icon: Icon, text } = KINDS[kind];
  return (
    <Icon
      data-slot="kind-icon"
      aria-hidden
      className={cn("size-3.5 shrink-0", text, className)}
      {...props}
    />
  );
}

export { KindIcon, KINDS, KIND_ORDER };
export type { Kind, KindIconProps };
